import { useEffect, useMemo, useState } from 'react';
import {
  useBillingReconciliationAppSheetAccounts,
  useBillingReconciliationBacAccounts,
  useBillingReconciliationCatalog,
  useBillingReconciliationFullEvidence,
  useCreateBillingReconciliationCredit,
} from '../../hooks/api/useBillingReconciliationHooks.js';
import { creditNoteForServiceMonth } from './creditNotePolicy.js';
import {
  EMPTY_CREDIT_ROWS,
  accountChoicesForBac,
  accountOptionsFromRows,
  buildCreditPayload,
  catalogOptionsFromRows,
  parseCreditAmount,
  productSelectionFields,
} from './manualCreditFormModel.js';

const useDebouncedValue = (value, delayMs = 250) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeout);
  }, [delayMs, value]);
  return debounced;
};

export const useManualCreditForm = ({ open, onClose, runId, program, serviceMonth }) => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [customProductCode, setCustomProductCode] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [fullCreditAmount, setFullCreditAmount] = useState('');
  const [notes, setNotes] = useState(() => creditNoteForServiceMonth(serviceMonth));
  const [error, setError] = useState('');
  const debouncedAccountSearch = useDebouncedValue(accountSearch, 250);

  const accountsQuery = useBillingReconciliationAppSheetAccounts({
    runId,
    search: debouncedAccountSearch,
    limit: 25,
  }, { enabled: open && Boolean(runId && debouncedAccountSearch.trim().length >= 2) });
  const bacAccountsQuery = useBillingReconciliationBacAccounts({
    runId,
    bac: selectedAccount?.bac || '',
    limit: 100,
  }, { enabled: open && Boolean(runId && selectedAccount?.bac) });
  const catalogQuery = useBillingReconciliationCatalog({ program, limit: 500 }, { enabled: open && Boolean(program) });
  const evidenceQuery = useBillingReconciliationFullEvidence({
    runId,
    bac: selectedAccount?.bac,
  }, { enabled: open && Boolean(runId && selectedAccount?.bac) });
  const createCredit = useCreateBillingReconciliationCredit();

  const accountOptions = useMemo(
    () => {
      const rows = accountOptionsFromRows(accountsQuery.data || EMPTY_CREDIT_ROWS);
      if (!selectedAccount?.bac) return rows;
      const exists = rows.some((row) => (
        row.bac === selectedAccount.bac
        && row.salesforceAccountId === selectedAccount.salesforceAccountId
      ));
      return exists ? rows : [selectedAccount, ...rows];
    },
    [accountsQuery.data, selectedAccount],
  );
  const accountChoices = useMemo(
    () => accountChoicesForBac({ rows: bacAccountsQuery.data || EMPTY_CREDIT_ROWS, selectedAccount }),
    [bacAccountsQuery.data, selectedAccount],
  );
  const catalogOptions = useMemo(
    () => catalogOptionsFromRows(catalogQuery.data || EMPTY_CREDIT_ROWS),
    [catalogQuery.data],
  );

  const reset = () => {
    setSelectedAccount(null);
    setAccountSearch('');
    setSelectedProduct(null);
    setCustomProductCode('');
    setCustomPrice('');
    setFullCreditAmount('');
    setNotes(creditNoteForServiceMonth(serviceMonth));
    setError('');
  };

  const close = () => {
    reset();
    onClose();
  };

  const selectAccount = (value) => {
    setSelectedAccount(value);
    setAccountSearch(value?.label || '');
    setSelectedProduct(null);
  };

  const selectProduct = (product) => {
    const { code, price } = productSelectionFields(product);
    setSelectedProduct(product);
    setCustomProductCode(code);
    setCustomPrice(price ? String(price) : '');
    if (!fullCreditAmount && price) setFullCreditAmount(String(Math.abs(Number(price))));
  };

  const updateCustomProductCode = (value = '') => {
    setCustomProductCode(value);
    const catalogMatch = catalogOptions.find((option) => option.code.trim().toUpperCase() === value.trim().toUpperCase());
    if (catalogMatch) {
      setSelectedProduct({
        source: 'Catalog',
        code: catalogMatch.code,
        name: catalogMatch.name,
        price: catalogMatch.price,
        row: catalogMatch.row,
      });
      setCustomPrice(catalogMatch.price ? String(catalogMatch.price) : '');
      if (!fullCreditAmount && catalogMatch.price) setFullCreditAmount(String(Math.abs(Number(catalogMatch.price))));
      return;
    }
    const inheritedCode = selectedProduct?.productCode || selectedProduct?.code || '';
    if (selectedProduct && value.trim().toUpperCase() !== inheritedCode.trim().toUpperCase()) {
      setSelectedProduct(null);
    }
  };

  const selectCatalogProduct = (value) => {
    if (!value || typeof value === 'string') {
      updateCustomProductCode(value || '');
      return;
    }
    selectProduct({ source: 'Catalog', code: value.code, name: value.name, price: value.price, row: value.row });
  };

  const canSubmit = Boolean(
    selectedAccount?.bac
    && customProductCode.trim()
    && Number.isFinite(parseCreditAmount(fullCreditAmount))
    && parseCreditAmount(fullCreditAmount) !== 0,
  );

  const submit = async () => {
    setError('');
    const code = customProductCode.trim();
    if (!selectedAccount?.bac) {
      setError('Select a BAC or Salesforce account first.');
      return;
    }
    if (!code) {
      setError('Choose a product or enter a product code.');
      return;
    }
    const parsedCreditAmount = parseCreditAmount(fullCreditAmount);
    if (!Number.isFinite(parsedCreditAmount) || parsedCreditAmount === 0) {
      setError('Enter the full credit amount.');
      return;
    }
    try {
      await createCredit.mutateAsync(buildCreditPayload({
        runId,
        selectedAccount,
        selectedProduct,
        customProductCode: code,
        customPrice,
        fullCreditAmount,
        notes,
      }));
      close();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || 'Credit could not be added.');
    }
  };

  return {
    accountChoices,
    accountOptions,
    accountSearch,
    accountsQuery,
    bacAccountsQuery,
    canSubmit,
    catalogOptions,
    catalogQuery,
    close,
    createCredit,
    customPrice,
    customProductCode,
    error,
    evidenceQuery,
    fullCreditAmount,
    notes,
    selectedAccount,
    selectedProduct,
    selectAccount,
    selectCatalogProduct,
    selectProduct,
    setAccountSearch,
    setCustomPrice,
    setFullCreditAmount,
    setNotes,
    submit,
    updateCustomProductCode,
  };
};
